// src/pages/Wallet/hooks/useNFTData.js - Refactored Version
import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { WalletContext } from '../../../contexts/WalletContext';
import { NetworkContext } from '../../../contexts/NetworkContext';
import { AuthContext } from '../../../contexts/AuthContext';
import { fetchNFTs } from '../../../services/nftFetcher';
import { isValidNFT, sortNFTs } from '../../../services/nftFetcher/nftFormatUtils';
import { 
  getCachedNFTs, 
  cacheNFTs, 
  clearCachedNFTs, 
  hasCachedNFTs,
  getNFTCacheInfo 
} from '../../../utils/nftCacheUtils';

// Constants
const REFRESH_THROTTLE_MS = 2000;
const LOADING_DELAY_MS = 800;

/**
 * Hook to manage NFT data with intelligent caching
 * Provides automatic caching, instant chain switching, and smart refresh logic
 */
export const useNFTData = () => {
  // Contexts
  const { currentWallet, isInitialized } = useContext(WalletContext);
  const { currentChain } = useContext(NetworkContext);
  const { isAuthenticated } = useContext(AuthContext);
  
  // State
  const [nfts, setNfts] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(0);
  const [error, setError] = useState(null);
  const [fromCache, setFromCache] = useState(false);

  // Memoized values
  const isWalletReady = useMemo(() => 
    isInitialized && isAuthenticated && currentWallet && currentChain,
    [isInitialized, isAuthenticated, currentWallet, currentChain]
  );

  const cacheKey = useMemo(() => {
    if (!currentWallet?.address || !currentChain?.key) return null;
    return `${currentWallet.address.toLowerCase()}-${currentChain.key}`;
  }, [currentWallet?.address, currentChain?.key]);

  // Cache operations
  const loadFromCache = useCallback(() => {
    if (!currentWallet?.address || !currentChain?.key) {
      setNfts([]);
      setFromCache(false);
      return false;
    }

    const cachedNFTs = getCachedNFTs(currentWallet.address, currentChain.key);
    if (!cachedNFTs) {
      setFromCache(false);
      return false;
    }

    console.log(`📁 Loaded ${cachedNFTs.length} NFTs from cache for ${currentChain.key}`);
    
    const validNFTs = cachedNFTs.filter(isValidNFT);
    const sortedNFTs = sortNFTs(validNFTs);
    
    setNfts(sortedNFTs);
    setFromCache(true);
    setError(null);
    return true;
  }, [currentWallet?.address, currentChain?.key]);

  const saveToCache = useCallback((nftData) => {
    if (currentWallet?.address && currentChain?.key && nftData) {
      cacheNFTs(currentWallet.address, currentChain.key, nftData);
      console.log(`💾 Cached ${nftData.length} NFTs for ${currentChain.key}`);
    }
  }, [currentWallet?.address, currentChain?.key]);

  const clearCache = useCallback(() => {
    if (currentWallet?.address && currentChain?.key) {
      clearCachedNFTs(currentWallet.address, currentChain.key);
      console.log(`🗑️ Cleared NFT cache for ${currentChain.key}`);
    }
  }, [currentWallet?.address, currentChain?.key]);

  // Network operations
  const fetchFromNetwork = useCallback(async () => {
    if (!currentChain || !currentWallet) {
      throw new Error('Wallet or chain not available');
    }

    console.log(`🌐 Fetching NFTs from network for ${currentChain.key}`);
    
    const fetchedNFTs = await fetchNFTs(currentChain.key, currentWallet.address);
    const validNFTs = fetchedNFTs.filter(isValidNFT);
    const sortedNFTs = sortNFTs(validNFTs);
    
    setNfts(sortedNFTs);
    setFromCache(false);
    saveToCache(sortedNFTs);
    
    console.log(`✅ Loaded ${sortedNFTs.length} NFTs from network`);
    return sortedNFTs;
  }, [currentChain, currentWallet, saveToCache]);

  // Main refresh logic
  const refreshNFTs = useCallback(async (options = {}) => {
    const { bypassCache = false, silent = false } = options;
    
    if (!isWalletReady) {
      setNfts([]);
      setFromCache(false);
      return;
    }

    // Try cache first unless bypassing
    if (!bypassCache && loadFromCache()) {
      return;
    }

    // Throttle refresh calls
    const now = Date.now();
    if (!bypassCache && now - lastRefreshTime < REFRESH_THROTTLE_MS) {
      return;
    }

    if (!silent) {
      setIsRefreshing(true);
      setLastRefreshTime(now);
    }

    try {
      setError(null);
      await fetchFromNetwork();
    } catch (err) {
      console.error('❌ Error refreshing NFTs:', err);
      setError(err.message);
      
      // Fallback to cache if network fails
      if (!loadFromCache()) {
        setNfts([]);
        setFromCache(false);
      }
    } finally {
      if (!silent) {
        // Small delay to show loading state
        setTimeout(() => setIsRefreshing(false), LOADING_DELAY_MS);
      }
    }
  }, [isWalletReady, loadFromCache, lastRefreshTime, fetchFromNetwork]);

  // Public API methods
  const refreshAll = useCallback(() => 
    refreshNFTs({ bypassCache: false }), 
    [refreshNFTs]
  );

  const forceRefresh = useCallback(async () => {
    console.log('🔄 Force refreshing NFTs (bypassing cache)');
    clearCache();
    await refreshNFTs({ bypassCache: true });
  }, [clearCache, refreshNFTs]);

  const silentRefresh = useCallback(() => 
    refreshNFTs({ bypassCache: false, silent: true }), 
    [refreshNFTs]
  );

  // NFT query helpers
  const getNFTById = useCallback((contractAddress, tokenId) => {
    return nfts.find(nft => 
      nft.nft.contractAddress.toLowerCase() === contractAddress.toLowerCase() &&
      nft.nft.tokenId === tokenId
    );
  }, [nfts]);

  const getNFTsByCollection = useCallback((contractAddress) => {
    return nfts.filter(nft => 
      nft.nft.contractAddress.toLowerCase() === contractAddress.toLowerCase()
    );
  }, [nfts]);

  const getCollections = useCallback(() => {
    const collectionsMap = new Map();
    
    nfts.forEach(nft => {
      const address = nft.nft.contractAddress.toLowerCase();
      
      if (!collectionsMap.has(address)) {
        collectionsMap.set(address, {
          contractAddress: nft.nft.contractAddress,
          name: nft.nft.collectionName || nft.nft.name || 'Unknown Collection',
          count: 0,
          firstNFT: nft
        });
      }
      
      collectionsMap.get(address).count += 1;
    });
    
    return Array.from(collectionsMap.values());
  }, [nfts]);

  // Debug helpers
  const getCacheStatus = useCallback(() => {
    if (!cacheKey) return null;
    
    return {
      hasCached: hasCachedNFTs(currentWallet.address, currentChain.key),
      cacheInfo: getNFTCacheInfo(currentWallet.address, currentChain.key),
      fromCache,
      cacheKey,
      nftCount: nfts.length,
      isWalletReady
    };
  }, [cacheKey, currentWallet?.address, currentChain?.key, fromCache, nfts.length, isWalletReady]);

  const getStats = useCallback(() => ({
    totalNFTs: nfts.length,
    collections: getCollections().length,
    fromCache,
    lastRefresh: lastRefreshTime,
    hasError: !!error,
    isLoading: isRefreshing
  }), [nfts.length, getCollections, fromCache, lastRefreshTime, error, isRefreshing]);

  // Effects
  useEffect(() => {
    if (!isWalletReady) {
      setNfts([]);
      setFromCache(false);
      setError(null);
      return;
    }

    console.log(`🔄 Wallet/chain changed: ${currentChain.key}:${currentWallet.address}`);
    
    // Try cache first, fallback to network
    if (!loadFromCache()) {
      refreshNFTs({ bypassCache: false, silent: false });
    }
  }, [currentChain?.key, currentWallet?.address, isWalletReady, loadFromCache, refreshNFTs]);

  // Auto-refresh on app focus (optional)
  useEffect(() => {
    const handleFocus = () => {
      if (isWalletReady && document.visibilityState === 'visible') {
        // Silent refresh when app regains focus
        silentRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleFocus);
    return () => document.removeEventListener('visibilitychange', handleFocus);
  }, [isWalletReady, silentRefresh]);

  // Public API
  return {
    // Data
    nfts,
    isRefreshing,
    error,
    fromCache,
    
    // Actions
    refreshNFTs: refreshAll,
    refreshAll,
    forceRefresh,
    silentRefresh,
    
    // Queries
    getNFTById,
    getNFTsByCollection,
    getCollections,
    
    // Debug
    getCacheStatus,
    getStats
  };
};